import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { directusOperations } from '../../lib/directus';
import { getWIBTimeForDirectus } from '../../lib/timeUtils';

// Define the API state interface for general API operations
export interface APIState {
  // Connection status
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  
  // General API operations
  isLoading: boolean;
  error: string | null;
  
  // Dashboard data cache
  dashboardData: any | null;
  dashboardLastUpdated: string | null;
  
  // Employee stats cache
  employeeStats: any | null;
  statsLastUpdated: string | null;
}

const initialState: APIState = {
  isConnected: true, // Assume connected initially with APIService
  isConnecting: false,
  connectionError: null,
  
  isLoading: false,
  error: null,
  
  dashboardData: null,
  dashboardLastUpdated: null,
  
  employeeStats: null,
  statsLastUpdated: null,
};

// Async thunks for dashboard and common API operations
export const fetchDashboardData = createAsyncThunk(
  'api/fetchDashboardData',
  async (_, { rejectWithValue }) => {
    try {
      return await directusOperations.getDashboardData();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch dashboard data');
    }
  }
);

export const fetchEmployeeStats = createAsyncThunk(
  'api/fetchEmployeeStats',
  async (_, { rejectWithValue }) => {
    try {
      return await directusOperations.getEmployeeStats();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch employee stats');
    }
  }
);

// Health check for API connection
export const checkAPIConnection = createAsyncThunk(
  'api/checkConnection',
  async (_, { rejectWithValue }) => {
    try {
      // Simple check by attempting to get current user
      await directusOperations.getCurrentUser();
      return { success: true };
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'API connection failed');
    }
  }
);

const apiSlice = createSlice({
  name: 'api',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.connectionError = null;
    },
    
    setConnectionStatus: (state, action: PayloadAction<boolean>) => {
      state.isConnected = action.payload;
      if (action.payload) {
        state.connectionError = null;
      }
    },
    
    clearDashboardCache: (state) => {
      state.dashboardData = null;
      state.dashboardLastUpdated = null;
    },
    
    clearStatsCache: (state) => {
      state.employeeStats = null;
      state.statsLastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check API connection
      .addCase(checkAPIConnection.pending, (state) => {
        state.isConnecting = true;
        state.connectionError = null;
      })
      .addCase(checkAPIConnection.fulfilled, (state) => {
        state.isConnecting = false;
        state.isConnected = true;
        state.connectionError = null;
      })
      .addCase(checkAPIConnection.rejected, (state, action) => {
        state.isConnecting = false;
        state.isConnected = false;
        state.connectionError = action.payload as string;
      })
      
      // Fetch dashboard data
      .addCase(fetchDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDashboardData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboardData = action.payload;
        state.dashboardLastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch employee stats
      .addCase(fetchEmployeeStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employeeStats = action.payload;
        state.statsLastUpdated = getWIBTimeForDirectus();
        state.error = null;
      })
      .addCase(fetchEmployeeStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setConnectionStatus,
  clearDashboardCache,
  clearStatsCache,
} = apiSlice.actions;

// Selectors
export const selectAPIState = (state: { api: APIState }) => state.api;
export const selectIsAPIConnected = (state: { api: APIState }) => state.api.isConnected;
export const selectAPILoading = (state: { api: APIState }) => state.api.isLoading;
export const selectAPIError = (state: { api: APIState }) => state.api.error;
export const selectConnectionError = (state: { api: APIState }) => state.api.connectionError;
export const selectDashboardData = (state: { api: APIState }) => state.api.dashboardData;
export const selectEmployeeStats = (state: { api: APIState }) => state.api.employeeStats;
export const selectDashboardLastUpdated = (state: { api: APIState }) => state.api.dashboardLastUpdated;
export const selectStatsLastUpdated = (state: { api: APIState }) => state.api.statsLastUpdated;

// Cache validation helpers
export const selectIsDashboardCacheValid = (state: { api: APIState }, maxAge: number = 5 * 60 * 1000) => {
  if (!state.api.dashboardLastUpdated) return false;
  const age = Date.now() - new Date(state.api.dashboardLastUpdated).getTime();
  return age < maxAge;
};

export const selectIsStatsCacheValid = (state: { api: APIState }, maxAge: number = 5 * 60 * 1000) => {
  if (!state.api.statsLastUpdated) return false;
  const age = Date.now() - new Date(state.api.statsLastUpdated).getTime();
  return age < maxAge;
};

export default apiSlice.reducer;